// frontend/src/admin/pages/AdminVouchersPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

import { listVouchers, deactivateVoucher } from "../api";
import CreateVoucherModal from "../components/CreateVoucherModal";

const TYPE_LABEL = {
  percent: "Percent",
  fixed_usd: "USD",
  free_months: "Months",
};

function fmtValue(v) {
  const n = Number(v.discount_value);
  if (v.discount_type === "percent") return `${n}%`;
  if (v.discount_type === "fixed_usd") return `$${n}`;
  return `${n} mo`;
}

function fmtDate(s) {
  return s ? new Date(s).toLocaleDateString() : "—";
}

function AdminVouchersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listVouchers({ per_page: 100 });
      setRows(data.items ?? []);
      setError(null);
    } catch {
      setError("Failed to load vouchers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDeactivate = async (id) => {
    try {
      await deactivateVoucher(id);
      toast.success("Voucher deactivated.");
      await load();
    } catch {
      toast.error("Failed to deactivate.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-slate-900">Vouchers</h1>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-500"
        >
          Create Voucher
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No vouchers yet.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-right px-4 py-2">Value</th>
                <th className="text-right px-4 py-2">Redemptions</th>
                <th className="text-left px-4 py-2">Valid Until</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/vouchers/${v.id}`}
                      className="font-mono font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      {v.code}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {TYPE_LABEL[v.discount_type] ?? v.discount_type}
                  </td>
                  <td className="px-4 py-2 text-right">{fmtValue(v)}</td>
                  <td className="px-4 py-2 text-right">
                    {v.redeemed_count}
                    {v.max_redemptions ? ` / ${v.max_redemptions}` : ""}
                  </td>
                  <td className="px-4 py-2 text-slate-500">
                    {fmtDate(v.valid_until)}
                  </td>
                  <td className="px-4 py-2">
                    {v.is_active ? (
                      <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">
                        Active
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {v.is_active && (
                      <button
                        type="button"
                        onClick={() => onDeactivate(v.id)}
                        className="text-xs font-medium text-red-600 hover:text-red-500"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateVoucherModal
          onClose={() => setShowCreate(false)}
          onCreated={load}
        />
      )}
    </div>
  );
}

export default AdminVouchersPage;
